import { makeObservable, observable, runInAction, action } from "mobx";
import { ApiSchema, api } from "@/modules/api";
import { WithAppStore } from "@/store/types";
import { AppStore } from "@/store";
import { PerDeviceOnboardingInfoObservable } from "@/store/platform-info/PerDeviceOnboardingInfoObservable";
import { BaseError } from "@/domains/errors";
import { objectModule } from "@/modules/object";
import pRetry from "p-retry";

export class PlatformOnboardingInfoObservable {
  store: AppStore;
  perDeviceOnboardingInfo: PerDeviceOnboardingInfoObservable;
  shouldShowIcpRfi: boolean;
  shouldShowHeadsUpCoachMark: boolean;
  completedTaskIds: string[];
  allTasksCompletedAt: string | null;
  onboardingFlowVersion: "intent_v1" | null;
  onboardingIntent: "meetings" | "quick_thoughts" | null;
  intentOnboardingCompletedAt: string | null;

  constructor({
    perDeviceOnboardingInfo,
    shouldShowIcpRfi,
    shouldShowHeadsUpCoachMark,
    completedTaskIds = [],
    allTasksCompletedAt = null,
    onboardingFlowVersion = null,
    onboardingIntent = null,
    intentOnboardingCompletedAt = null,
    store,
  }: {
    perDeviceOnboardingInfo: PerDeviceOnboardingInfoObservable;
    shouldShowIcpRfi?: boolean;
    shouldShowHeadsUpCoachMark?: boolean;
    completedTaskIds?: string[];
    allTasksCompletedAt?: string | null;
    onboardingFlowVersion?: "intent_v1" | null;
    onboardingIntent?: "meetings" | "quick_thoughts" | null;
    intentOnboardingCompletedAt?: string | null;
  } & WithAppStore) {
    this.store = store;
    this.perDeviceOnboardingInfo = perDeviceOnboardingInfo;
    this.shouldShowIcpRfi = shouldShowIcpRfi ?? false;
    this.shouldShowHeadsUpCoachMark = shouldShowHeadsUpCoachMark ?? false;
    this.completedTaskIds = completedTaskIds;
    this.allTasksCompletedAt = allTasksCompletedAt;
    this.onboardingFlowVersion = onboardingFlowVersion;
    this.onboardingIntent = onboardingIntent;
    this.intentOnboardingCompletedAt = intentOnboardingCompletedAt;

    makeObservable(this, {
      store: false,
      perDeviceOnboardingInfo: false,
      shouldShowIcpRfi: observable,
      shouldShowHeadsUpCoachMark: observable,
      completedTaskIds: observable,
      allTasksCompletedAt: observable,
      onboardingFlowVersion: observable,
      onboardingIntent: observable,
      intentOnboardingCompletedAt: observable,
      hasCompletedTask: false,
      markTaskCompleted: action,
      completeOnboardingTasks: action,
      completeIcpRfiStep: action,
      debugReset: action,
    });
  }

  hasCompletedTask(taskId: string): boolean {
    return this.completedTaskIds.includes(taskId);
  }

  markTaskCompleted(taskId: string) {
    runInAction(() => {
      if (!this.completedTaskIds.includes(taskId)) {
        this.completedTaskIds.push(taskId);
      }
    });
  }

  /**
   * Persists account-shared onboarding completion so existing-user pages do not
   * replay on other devices after one device finishes them.
   */
  async completeOnboardingTasks(taskIds: string[]) {
    const incompleteTaskIds = taskIds.filter(
      (taskId) => !this.hasCompletedTask(taskId),
    );
    if (incompleteTaskIds.length === 0) {
      return;
    }

    runInAction(() => {
      this.completedTaskIds = [...this.completedTaskIds, ...incompleteTaskIds];
    });

    await pRetry(async () => {
      const response = await api.post(
        "/v2/me/platform-info/complete-onboarding-tasks",
        {
          body: { task_ids: incompleteTaskIds },
        },
      );

      if (response?.data?.onboarding_info) {
        const apiOnboardingInfo = response.data
          .onboarding_info as ApiSchema["PlatformInfoOnboarding"];

        const updated = PlatformOnboardingInfoObservable.createFromRemoteData({
          data: apiOnboardingInfo,
          store: this.store,
          perDeviceOnboardingInfo: this.perDeviceOnboardingInfo,
        });

        runInAction(() => {
          this.shouldShowIcpRfi = updated.shouldShowIcpRfi;
          this.shouldShowHeadsUpCoachMark = updated.shouldShowHeadsUpCoachMark;
          this.completedTaskIds = Array.from(
            new Set([...this.completedTaskIds, ...updated.completedTaskIds]),
          );
          this.allTasksCompletedAt = updated.allTasksCompletedAt;
          this.onboardingFlowVersion = updated.onboardingFlowVersion;
          this.onboardingIntent = updated.onboardingIntent;
          this.intentOnboardingCompletedAt =
            updated.intentOnboardingCompletedAt;
        });
        return;
      }

      if (response?.error) {
        const err = new BaseError({
          message: "Failed to complete onboarding tasks for current account.",
          info: {
            response: objectModule.safeAsJson(response),
          },
        });
        throw err;
      }
    });
  }

  async completeIcpRfiStep(completedSteps: string[] = []) {
    for (const step of completedSteps) {
      this.markTaskCompleted(step);
    }
    if (completedSteps.includes("referrer")) {
      return this.store.platformInfo.dismissIcpRfi({ completedSteps });
    }
  }

  async debugReset() {
    runInAction(() => {
      this.shouldShowIcpRfi = true;
      this.completedTaskIds = [];
    });

    await pRetry(async () => {
      const response = await api.post("/v2/me/platform-info/reset-icp-rfi");

      if (response?.data?.onboarding_info) {
        const apiOnboardingInfo = response.data
          .onboarding_info as ApiSchema["PlatformInfoOnboarding"];

        const updated = PlatformOnboardingInfoObservable.createFromRemoteData({
          data: apiOnboardingInfo,
          store: this.store,
          perDeviceOnboardingInfo: this.perDeviceOnboardingInfo,
        });

        runInAction(() => {
          this.shouldShowIcpRfi = updated.shouldShowIcpRfi;
          this.shouldShowHeadsUpCoachMark = updated.shouldShowHeadsUpCoachMark;
          this.completedTaskIds = updated.completedTaskIds;
          this.allTasksCompletedAt = updated.allTasksCompletedAt;
          this.onboardingFlowVersion = updated.onboardingFlowVersion;
          this.onboardingIntent = updated.onboardingIntent;
          this.intentOnboardingCompletedAt =
            updated.intentOnboardingCompletedAt;
        });
        return;
      }

      if (response?.error) {
        const err = new BaseError({
          message: "Failed to reset ICP RFI for current account.",
          info: {
            response: objectModule.safeAsJson(response),
          },
        });
        throw err;
      }
    });
  }

  public static createFromRemoteData({
    data,
    store,
    perDeviceOnboardingInfo,
  }: {
    data: ApiSchema["PlatformInfoOnboarding"];
    perDeviceOnboardingInfo: PerDeviceOnboardingInfoObservable;
  } & WithAppStore) {
    // Type-safe property access with fallbacks for new RFI properties
    type ExtendedOnboardingData = ApiSchema["PlatformInfoOnboarding"] & {
      completed_task_ids?: string[];
      should_show_rfi?: boolean;
      should_show_icp_rfi?: boolean;
      should_show_heads_up_coach_mark?: boolean;
      rfi_completed_steps?: string[];
      onboarding_flow_version?: string | null;
      onboarding_intent?: string | null;
      intent_onboarding_completed_at?: string | null;
    };
    const extendedData = data as ExtendedOnboardingData;
    const completedTaskIds =
      extendedData.completed_task_ids ?? extendedData.rfi_completed_steps;

    return new PlatformOnboardingInfoObservable({
      shouldShowIcpRfi:
        extendedData.should_show_icp_rfi ??
        extendedData.should_show_rfi ??
        false,
      shouldShowHeadsUpCoachMark:
        extendedData.should_show_heads_up_coach_mark ?? false,
      completedTaskIds: completedTaskIds ?? [],
      allTasksCompletedAt: extendedData.all_tasks_completed_at ?? null,
      onboardingFlowVersion:
        extendedData.onboarding_flow_version === "intent_v1"
          ? "intent_v1"
          : null,
      onboardingIntent:
        extendedData.onboarding_intent === "meetings" ||
        extendedData.onboarding_intent === "quick_thoughts"
          ? extendedData.onboarding_intent
          : null,
      intentOnboardingCompletedAt:
        extendedData.intent_onboarding_completed_at ?? null,
      store,
      perDeviceOnboardingInfo,
    });
  }
}
